import { useCentralizedMe } from '@crehana/auth';
import { format } from 'date-fns';

import { getCurrentTimeZone } from '@/shared/utils/time-zone';

import type { TAttendanceCardDayShift } from '../../types';
import { useTimeAndAttendanceUserMyRequestsGetShiftForDateQuery } from './get-shift-for-date.v5.generated';

const WIRE_DATE_FORMAT = 'yyyy-MM-dd';

// TIM-663 — `shifts` carries three states, not two: `undefined` means "not
// resolved yet" (no date picked, still loading, or the query failed). An
// empty array means "resolved: the backend says this day has no shift". A
// caller must never read `undefined` as an empty array — that turns a
// pending or failed query into a false "no shift" claim.
export const useGetShiftForDate = (date: Date | undefined) => {
  const { currentPageOrganization } = useCentralizedMe();
  const orgId = currentPageOrganization?.id ?? 0;
  const timezone = getCurrentTimeZone();
  const wireDate = date ? format(date, WIRE_DATE_FORMAT) : undefined;

  const { data, loading, error } =
    useTimeAndAttendanceUserMyRequestsGetShiftForDateQuery({
      variables: {
        input: {
          centralized_organization_id: orgId,
          start_date: wireDate ?? '',
          end_date: wireDate ?? '',
          timezone,
        },
      },
      context: { clientName: 'v5' },
      skip: !wireDate,
    });

  const result = data?.time?.attendance_marks?.get_attendance_card;
  const payload =
    result?.__typename === 'TimeGetAttendanceCardPayload' ? result : undefined;
  const hasError = Boolean(error || (!loading && result && !payload));
  const shifts: Array<TAttendanceCardDayShift> | undefined = payload
    ? (payload.days[0]?.shifts ?? [])
    : undefined;

  return {
    shifts,
    loading,
    error: hasError,
  };
};
